import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';
import * as Rx from 'rxjs';

export interface LocationEvent {
  hash: string; // a '#' followed by the fragment identifier of the URL.
  hostname: string; // the domain of the URL
  href: string; // the entire URL
  origin: string; // the origin of the specific location (protocol + :// + host)
  pathname: string; // an initial '/' followed by the path of the URL
  search: string; // a '?' followed by the parameters or "querystring" of the URL
}

/*
 * Reactive & server-side friendly version of window.location
 * See https://developer.mozilla.org/en-US/docs/Web/API/Location
 */
@Injectable({
  providedIn: 'root',
})
export class Location {
  private routeLoaded = false;
  private locationSubject$ = new Rx.ReplaySubject<LocationEvent>(1);

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional()
    @Inject(REQUEST)
    private request: Request & { _SSR_handleRedirect(url: string): void },
  ) {}

  /*
   * Emits a stream of LocationEvent
   */
  valueChanges(): Rx.Observable<LocationEvent> {
    return this.locationSubject$.asObservable();
  }

  /**
   * SSR-friendly version of Router::navigateByUrl()
   */
  navigateByUrl(url: string | UrlTree): Rx.Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      // tslint:disable-next-line:no-navigate-by-url
      return Rx.from(this.router.navigateByUrl(url));
    }
    this.request._SSR_handleRedirect(`${this.origin}${url}`);
    // tslint:disable-next-line:no-navigate-by-url
    return Rx.from(this.router.navigateByUrl('/internal/blank'));
  }

  /**
   * Sets the location to a provided url. This will leave the webapp, DO NOT
   * use it for internal redirects, use navigateByUrl instead
   */
  redirect(url: string): Rx.Observable<boolean> {
    if (isPlatformBrowser(this.platformId)) {
      if (this.routeLoaded) {
        window.location.assign(url);
      } else {
        window.location.replace(url);
      }
      return Rx.NEVER as Rx.Observable<boolean>;
    }
    this.request._SSR_handleRedirect(url);
    // tslint:disable-next-line:no-navigate-by-url
    return Rx.from(this.router.navigateByUrl('/internal/blank'));
  }

  /*
   * Returns the current origin in a platform friendly way
   */
  get origin(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.origin;
    }
    return `${this.request.protocol}://${this.host}`;
  }

  /*
   * Returns the current host, that is the hostname, a ':', and the port of the
   * URL, in a platform friendly way
   */
  get host(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.host;
    }
    // FIXME: we should be using `req.host` here but Express incorrectly strips
    // off the port number, which breaks local dev. This has been fixed in
    // Express 5 -> https://expressjs.com/en/guide/migrating-5.html#req.host
    // Note: X-Forwarded-Host is normally only ever a single value, but Fastly
    // seems to append the values.
    const header =
      this.request.get('X-Forwarded-Host') ||
      (this.request.get('Host') as string);
    const index = header.indexOf(',');
    return index !== -1 ? header.substring(0, index).trim() : header.trim();
  }

  /*
   * Returns the current hostname, that is the domain of the URL, in a platform
   * friendly way
   */
  get hostname(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.hostname;
    }
    return this.request.hostname;
  }

  /*
   * Returns the current (absolute) URL in a platform friendly way
   */
  get href(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.href;
    }
    return `${this.origin}${this.request.originalUrl}`;
  }

  /*
   * Returns the current URL in a platform friendly way
   */
  get url(): string {
    if (isPlatformBrowser(this.platformId)) {
      return `${window.location.pathname}${window.location.search}`;
    }
    return this.request.originalUrl;
  }

  /**
   * Returns the current pathname in a platform friendly way
   */
  get pathname(): string {
    if (isPlatformBrowser(this.platformId)) {
      return window.location.pathname;
    }
    return this.request.path;
  }

  /**
   * [PRIVATE] Exported private API.
   * To be used only by the LocationComponent to push new locations
   */
  _setLocation(pathname: string, hash: string, search: string): void {
    this.locationSubject$.next({
      hash,
      hostname: this.hostname,
      href: `${this.origin}${pathname}`,
      origin: this.origin,
      pathname,
      search,
    });
  }

  /**
   * [PRIVATE] exported private API
   * To be used only by the LocationComponent to mark that routes are loaded
   */
  _routeHasLoaded() {
    this.routeLoaded = true;
  }
}
